// useVerifyEmail Hook
// Location: src/hooks/useVerifyEmail.ts
// Purpose: Verify signup confirmation OTP and hydrate session

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

export const useVerifyEmail = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const email = searchParams.get('email') || '';
  const userId = searchParams.get('userId') || '';

  const [otpCode, setOtpCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [attemptsRemaining, setAttemptsRemaining] = useState<number | null>(null);
  const [canResend, setCanResend] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(60);

  // Guard: both params must be present
  useEffect(() => {
    if (!email || !userId) {
      toast.error(t('auth.errors.invalidOTPLink') || 'Invalid verification link');
      navigate('/register');
    }
  }, [email, userId, navigate, t]);

  // Resend cooldown timer — starts at 60s on page load since a code was just sent
  useEffect(() => {
    if (resendCooldown <= 0) {
      setCanResend(true);
      return;
    }
    const timer = setTimeout(() => setResendCooldown((prev) => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleOtpChange = (value: string) => {
    setOtpCode(value.replace(/\D/g, '').substring(0, 6));
  };

  const verifyOTP = async () => {
    if (otpCode.length !== 6) {
      toast.error(t('auth.errors.otpInvalid') || 'Please enter a valid 6-digit code');
      return;
    }

    setIsVerifying(true);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/auth-verify-signup-otp`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            user_id: userId,
            otp_code: otpCode,
          }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        if (result.attempts_remaining !== undefined) {
          setAttemptsRemaining(result.attempts_remaining);
        }
        throw new Error(result.error || 'Verification failed');
      }

      // Exchange the recovery token for a real session
      const { token_hash, type } = result.auth;

      if (!token_hash) {
        throw new Error('Auth token missing from server response');
      }

      const { error: sessionError } = await supabase.auth.verifyOtp({
        type: type || 'recovery',
        token_hash,
      });

      if (sessionError) {
        throw sessionError;
      }

      // Session is now live. Write referred_by_code if one was captured at
      // registration. This is the earliest point where auth.uid() is valid
      // and RLS will allow the authenticated UPDATE to succeed.
      const pendingReferralCode = sessionStorage.getItem('pending_referral_code');
      if (pendingReferralCode) {
        const { error: referralError } = await supabase
          .from('profiles')
          .update({ referred_by_code: pendingReferralCode })
          .eq('id', userId);

        if (referralError) {
          // Non-fatal — user proceeds to onboarding regardless
          console.error('useVerifyEmail: failed to write referred_by_code:', referralError);
        } else {
          sessionStorage.removeItem('pending_referral_code');
        }
      }

      toast.success(t('auth.otp.verificationSuccess') || 'Email confirmed successfully');
      navigate('/onboarding');

    } catch (error: any) {
      const msg = error.message?.toLowerCase() || '';

      if (msg.includes('incorrect')) {
        toast.error(t('auth.errors.otpIncorrect') || 'Incorrect code. Please try again.');
      } else if (msg.includes('expired')) {
        toast.error(t('auth.errors.otpExpired') || 'Code expired. Please request a new one.');
      } else if (msg.includes('maximum') || msg.includes('attempts')) {
        toast.error(t('auth.errors.otpMaxAttempts') || 'Too many attempts. Please request a new code.');
      } else {
        toast.error(error.message || t('auth.errors.generic'));
      }
    } finally {
      setIsVerifying(false);
    }
  };

  const resendOTP = async () => {
    if (!canResend || isResending) return;

    setIsResending(true);
    setCanResend(false);

    try {
      // i18n language to pass to the email Edge Function
      const language = localStorage.getItem('i18nextLng') || 'en';

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/auth-send-signup-otp`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            user_id: userId,
            email,
            language,
          }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to resend code');
      }

      toast.success(t('auth.login.otpSent') || 'New code sent to your email');
      setOtpCode('');
      setAttemptsRemaining(null);
      setResendCooldown(60);

    } catch (error: any) {
      toast.error(error.message || t('auth.errors.resendFailed') || 'Failed to resend code');
      setCanResend(true);
    } finally {
      setIsResending(false);
    }
  };

  return {
    email,
    userId,
    otpCode,
    isVerifying,
    isResending,
    attemptsRemaining,
    canResend,
    resendCooldown,
    handleOtpChange,
    verifyOTP,
    resendOTP,
  };
};